import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Frame, Plus, Search, FileText, Trash2, RotateCcw, EyeOff, Eye, Archive,
  Package, Loader2, Download, ExternalLink, Inbox, ChevronRight, Filter,
  MailQuestion, Copy,
} from 'lucide-react';
import { ActionButton, SectionHeader } from '../components/VisualAtoms';
import DeleteArtworkModal from '../components/DeleteArtworkModal';
import { badgeStyle } from '../theme/adminVisualSystem';
import {
  listArtworks,
  publishArtwork,
  hideArtwork,
  archiveArtwork,
  markSoldOut,
  markDraft,
  generateOfferPdf,
  listArtworkInquiries,
  updateInquiry,
} from '../lib/artworksApi';

// ─── Local tone tokens for artwork status ─────────────────────────
const STATUS_TONES = {
  draft:     { text: '#9a9a9a', border: '#333', bg: 'transparent', label: 'Draft' },
  published: { text: '#7ed4a0', border: 'rgba(126,212,160,0.4)', bg: 'rgba(126,212,160,0.08)', label: 'Published' },
  hidden:    { text: '#e4c07a', border: 'rgba(228,192,122,0.4)', bg: 'rgba(228,192,122,0.08)', label: 'Hidden' },
  sold_out:  { text: '#c89dff', border: 'rgba(200,157,255,0.42)', bg: 'rgba(200,157,255,0.08)', label: 'Sold out' },
  archived:  { text: '#666',    border: '#222', bg: 'transparent', label: 'Archived' },
};
const INQUIRY_TONES = {
  new:         { text: '#90E0EF', border: 'rgba(144,224,239,0.45)', bg: 'rgba(144,224,239,0.08)' },
  contacted:   { text: '#6ab6d4', border: 'rgba(106,182,212,0.4)',  bg: 'rgba(106,182,212,0.08)' },
  in_progress: { text: '#e4c07a', border: 'rgba(228,192,122,0.4)',  bg: 'rgba(228,192,122,0.08)' },
  fulfilled:   { text: '#7ed4a0', border: 'rgba(126,212,160,0.4)',  bg: 'rgba(126,212,160,0.08)' },
  closed:      { text: '#666',    border: '#222',                   bg: 'transparent' },
  spam:        { text: '#e28f8f', border: 'rgba(226,143,143,0.4)',  bg: 'rgba(226,143,143,0.08)' },
};

const CURRENCY_SYMBOL = { EUR: '€', USD: '$', GBP: '£', PLN: 'zł' };
function formatPrice(amount, currency) {
  if (amount == null || amount === '') return 'On request';
  const sym = CURRENCY_SYMBOL[currency] || '';
  const n = Number(amount);
  if (Number.isNaN(n)) return 'On request';
  if (currency === 'PLN') return `${n.toLocaleString('pl-PL', { minimumFractionDigits: 0 })} ${sym}`;
  return `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
}

function StatusPill({ status }) {
  const t = STATUS_TONES[status] || STATUS_TONES.draft;
  return (
    <span
      className="inline-flex items-center text-[10px] font-mono px-2 py-0.5 border uppercase tracking-[0.14em]"
      style={{ color: t.text, borderColor: t.border, background: t.bg }}
    >
      {t.label}
    </span>
  );
}

function InquiryPill({ status }) {
  const t = INQUIRY_TONES[status] || INQUIRY_TONES.new;
  return (
    <span
      className="inline-flex items-center text-[10px] font-mono px-2 py-0.5 border uppercase tracking-[0.14em]"
      style={{ color: t.text, borderColor: t.border, background: t.bg }}
    >
      {(status || 'new').replace('_', ' ')}
    </span>
  );
}

// ─── Stat tile ─────────────────────────────────────────────────────

function StatTile({ label, value, active, onClick, tone }) {
  const borderColor = active ? (tone?.border || '#90E0EF') : '#1a1a1a';
  const bg = active ? (tone?.bg || 'rgba(144,224,239,0.06)') : 'transparent';
  const text = active ? (tone?.text || '#90E0EF') : '#f6f6f6';
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ y: -1.5 }}
      whileTap={{ scale: 0.98 }}
      data-clickable="true"
      data-testid={`aw-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className="text-left px-4 py-3 border transition-colors duration-200"
      style={{ borderColor, background: bg }}
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#777]">{label}</div>
      <div className="mt-1 text-2xl font-outfit font-semibold" style={{ color: text }}>{value ?? '—'}</div>
    </motion.button>
  );
}

// ─── Main page ─────────────────────────────────────────────────────

export default function ArtworksPage() {
  const navigate = useNavigate();
  const outlet = useOutletContext?.() || {};
  const [tab, setTab] = useState('artworks'); // artworks | inquiries
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [pdfBusy, setPdfBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState(new Set());

  // Inquiries state
  const [inquiries, setInquiries] = useState([]);
  const [inquiryStats, setInquiryStats] = useState({});
  const [inquiryStatusFilter, setInquiryStatusFilter] = useState('all');

  // Permanent delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, title } | null

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listArtworks({ status: statusFilter, search });
      setRows(data.artworks || []);
      setStats(data.stats || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load artworks');
    } finally {
      setLoading(false);
    }
  };

  const refreshInquiries = async () => {
    try {
      const data = await listArtworkInquiries({ status: inquiryStatusFilter });
      setInquiries(data.inquiries || []);
      setInquiryStats(data.stats || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load inquiries');
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => {
    if (tab === 'inquiries') refreshInquiries();
    /* eslint-disable-next-line */
  }, [tab, inquiryStatusFilter]);

  // Search debounce
  useEffect(() => {
    const t = setTimeout(() => refresh(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const filtered = useMemo(() => rows, [rows]);

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((r) => r.id)));
  };

  const doRowAction = async (id, fn, successMsg) => {
    const busy = new Set(rowBusy); busy.add(id); setRowBusy(busy);
    try {
      await fn(id);
      toast.success(successMsg);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      const b2 = new Set(rowBusy); b2.delete(id); setRowBusy(b2);
    }
  };

  const doGeneratePdf = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one artwork');
      return;
    }
    setPdfBusy(true);
    const t = toast.loading(`Generating ${selected.size > 1 ? 'multi-artwork ' : ''}offer PDF…`);
    try {
      const ids = filtered.map((r) => r.id).filter((id) => selected.has(id));
      const data = await generateOfferPdf({
        artwork_ids: ids,
        currency: 'EUR',
      });
      toast.success(`Offer PDF generated (${ids.length} artwork${ids.length > 1 ? 's' : ''})`, { id: t });
      window.open(data.document.pdf_url, '_blank');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PDF generation failed', { id: t });
    } finally {
      setPdfBusy(false);
    }
  };

  const setInquiryStatus = async (id, newStatus) => {
    try {
      await updateInquiry(id, { status: newStatus });
      toast.success(`Inquiry marked ${newStatus.replace('_', ' ')}`);
      refreshInquiries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  // ─── Render ───────────────────────────────────────────────────

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Frame className="w-6 h-6 text-[#90E0EF]" />
          <div>
            <h1 className="text-2xl font-outfit font-semibold text-white tracking-tight">Artworks</h1>
            <p className="text-[11px] font-mono text-[#777] tracking-[0.15em] mt-0.5">
              PRINT PRODUCTS · INQUIRIES · OFFER SHEETS
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ActionButton
            data-testid="aw-add-artwork-btn"
            variant="cyan"
            icon={Plus}
            onClick={() => navigate('/admin/artworks/new')}
          >
            Add Artwork
          </ActionButton>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[#1a1a1a] mb-6">
        {[
          { id: 'artworks', label: 'Artworks', icon: Frame, count: stats.all ?? 0 },
          { id: 'inquiries', label: 'Inquiries', icon: MailQuestion, count: inquiryStats.all ?? 0 },
        ].map((t) => {
          const active = tab === t.id;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`aw-tab-${t.id}`}
              data-clickable="true"
              onClick={() => setTab(t.id)}
              className={`relative flex items-center gap-2 px-4 py-2.5 text-[11px] font-mono uppercase tracking-[0.14em] transition-colors duration-200 ${
                active ? 'text-[#90E0EF]' : 'text-[#888] hover:text-[#f6f6f6]'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
              <span className="ml-1 text-[10px] text-[#555]">({t.count})</span>
              {active && (
                <motion.span
                  layoutId="aw-tab-underline"
                  className="absolute -bottom-px left-0 right-0 h-px bg-[#90E0EF]"
                  style={{ boxShadow: '0 0 6px rgba(144,224,239,0.5)' }}
                />
              )}
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        {tab === 'artworks' && (
          <motion.div key="artworks" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {/* Status tiles */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
              <StatTile label="All"       value={stats.all}       active={statusFilter === 'all'}       onClick={() => setStatusFilter('all')} />
              <StatTile label="Draft"     value={stats.draft}     active={statusFilter === 'draft'}     onClick={() => setStatusFilter('draft')}     tone={STATUS_TONES.draft} />
              <StatTile label="Published" value={stats.published} active={statusFilter === 'published'} onClick={() => setStatusFilter('published')} tone={STATUS_TONES.published} />
              <StatTile label="Hidden"    value={stats.hidden}    active={statusFilter === 'hidden'}    onClick={() => setStatusFilter('hidden')}    tone={STATUS_TONES.hidden} />
              <StatTile label="Sold Out"  value={stats.sold_out}  active={statusFilter === 'sold_out'}  onClick={() => setStatusFilter('sold_out')}  tone={STATUS_TONES.sold_out} />
              <StatTile label="Archived"  value={stats.archived}  active={statusFilter === 'archived'}  onClick={() => setStatusFilter('archived')}  tone={STATUS_TONES.archived} />
            </div>

            {/* Toolbar */}
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#666]" />
                <input
                  data-testid="aw-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search title, year…"
                  className="w-full bg-[#0a0a0a] border border-[#1a1a1a] pl-9 pr-3 py-2 text-[12px] font-mono text-[#f6f6f6] placeholder:text-[#444] focus:outline-none focus:border-[#90E0EF]/40"
                />
              </div>

              <AnimatePresence>
                {selected.size > 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                    className="flex items-center gap-2"
                  >
                    <span className="text-[11px] font-mono text-[#90E0EF] mr-1">{selected.size} SELECTED</span>
                    <ActionButton
                      data-testid="aw-generate-pdf-btn"
                      variant="primary"
                      icon={pdfBusy ? Loader2 : FileText}
                      disabled={pdfBusy}
                      onClick={doGeneratePdf}
                    >
                      {pdfBusy ? 'Generating…' : `Generate PDF (${selected.size})`}
                    </ActionButton>
                    <ActionButton variant="ghost" icon={undefined} onClick={() => setSelected(new Set())}>
                      Clear
                    </ActionButton>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Table */}
            <div className="border border-[#1a1a1a] bg-[#0a0a0a]">
              {/* Table header */}
              <div className="grid grid-cols-[36px_72px_1fr_180px_120px_140px_100px_180px] gap-3 items-center px-4 py-2.5 border-b border-[#1a1a1a] text-[10px] font-mono uppercase tracking-[0.18em] text-[#555]">
                <label className="flex items-center justify-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onChange={toggleAll}
                    className="accent-[#90E0EF]"
                    data-testid="aw-select-all"
                  />
                </label>
                <span>Media</span>
                <span>Title / Artist</span>
                <span>Meta</span>
                <span className="text-right">Price</span>
                <span>Status</span>
                <span className="text-center">Inquiries</span>
                <span className="text-right">Actions</span>
              </div>

              {loading && (
                <div className="px-4 py-16 text-center">
                  <Loader2 className="inline w-4 h-4 animate-spin text-[#90E0EF]" />
                  <p className="text-[11px] font-mono text-[#666] mt-2">LOADING…</p>
                </div>
              )}

              {!loading && filtered.length === 0 && (
                <div className="px-4 py-16 text-center">
                  <Package className="inline w-8 h-8 text-[#333]" />
                  <p className="text-[12px] font-outfit text-[#777] mt-3">No artworks yet</p>
                  <p className="text-[11px] font-mono text-[#555] mt-1">Click &quot;Add Artwork&quot; to publish your first piece.</p>
                </div>
              )}

              {!loading && filtered.map((r) => {
                const busy = rowBusy.has(r.id);
                return (
                  <div
                    key={r.id}
                    data-testid={`aw-row-${r.id}`}
                    className="group grid grid-cols-[36px_72px_1fr_180px_120px_140px_100px_180px] gap-3 items-center px-4 py-3 border-b border-[#111] hover:bg-[#0d0d0d] cursor-pointer transition-colors duration-150"
                    onClick={() => navigate(`/admin/artworks/${r.id}`)}
                  >
                    <label className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() => toggleSelect(r.id)}
                        className="accent-[#90E0EF]"
                        data-testid={`aw-select-${r.id}`}
                      />
                    </label>
                    <div className="w-14 h-14 border border-[#1a1a1a] bg-[#050505] overflow-hidden">
                      {(r.thumbnail_url || r.media_url) ? (
                        <img src={r.thumbnail_url || r.media_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Frame className="w-4 h-4 text-[#333]" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] font-outfit text-white truncate">{r.title || 'Untitled'}</div>
                      <div className="text-[11px] font-mono text-[#888] tracking-[0.05em] truncate">{r.artist_name || '—'}</div>
                    </div>
                    <div className="text-[10px] font-mono text-[#666]">
                      {[r.year, r.medium].filter(Boolean).join(' · ') || '—'}
                    </div>
                    <div className="text-right text-[12px] font-mono text-[#f6f6f6]">
                      {formatPrice(r.price_amount, r.currency)}
                    </div>
                    <div><StatusPill status={r.status} /></div>
                    <div className="text-center text-[11px] font-mono text-[#90E0EF]">
                      {r.inquiry_count || 0}
                    </div>
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      {r.status !== 'published' && (
                        <button
                          data-testid={`aw-publish-${r.id}`}
                          disabled={busy}
                          onClick={() => doRowAction(r.id, publishArtwork, 'Published')}
                          className="p-1.5 border border-[#1a1a1a] hover:border-[#7ed4a0] text-[#7ed4a0] transition-colors disabled:opacity-40"
                          title="Publish"
                        >
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                      )}
                      {r.status === 'published' && (
                        <button
                          data-testid={`aw-hide-${r.id}`}
                          disabled={busy}
                          onClick={() => doRowAction(r.id, hideArtwork, 'Hidden')}
                          className="p-1.5 border border-[#1a1a1a] hover:border-[#e4c07a] text-[#e4c07a] transition-colors disabled:opacity-40"
                          title="Hide"
                        >
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <EyeOff className="w-3.5 h-3.5" />}
                        </button>
                      )}
                      {r.status !== 'sold_out' && r.status === 'published' && (
                        <button
                          data-testid={`aw-soldout-${r.id}`}
                          disabled={busy}
                          onClick={() => doRowAction(r.id, markSoldOut, 'Marked sold out')}
                          className="p-1.5 border border-[#1a1a1a] hover:border-[#c89dff] text-[#c89dff] transition-colors disabled:opacity-40"
                          title="Mark sold out"
                        >
                          <Package className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {r.status !== 'archived' && (
                        <button
                          data-testid={`aw-archive-${r.id}`}
                          disabled={busy}
                          onClick={() => doRowAction(r.id, archiveArtwork, 'Archived')}
                          className="p-1.5 border border-[#1a1a1a] hover:border-[#444] text-[#888] hover:text-[#f6f6f6] transition-colors disabled:opacity-40"
                          title="Archive"
                        >
                          <Archive className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {r.status === 'archived' && (
                        <button
                          data-testid={`aw-restore-${r.id}`}
                          disabled={busy}
                          onClick={() => doRowAction(r.id, markDraft, 'Restored to draft')}
                          className="p-1.5 border border-[#1a1a1a] hover:border-[#90E0EF] text-[#90E0EF] transition-colors disabled:opacity-40"
                          title="Restore to draft"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        data-testid={`aw-delete-${r.id}`}
                        disabled={busy}
                        onClick={() => setDeleteTarget({ id: r.id, title: r.title })}
                        className="p-1.5 border border-[#1a1a1a] hover:border-[#e28f8f] text-[#666] hover:text-[#e28f8f] transition-colors disabled:opacity-40"
                        title="Delete permanently"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <ChevronRight className="w-3.5 h-3.5 text-[#333] group-hover:text-[#90E0EF] transition-colors ml-1" />
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {tab === 'inquiries' && (
          <motion.div key="inquiries" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {/* Inquiry status tiles */}
            <div className="flex flex-wrap gap-2 mb-6">
              {['all', 'new', 'contacted', 'in_progress', 'fulfilled', 'closed', 'spam'].map((s) => {
                const active = inquiryStatusFilter === s;
                const tone = INQUIRY_TONES[s];
                return (
                  <button
                    key={s}
                    data-testid={`aw-inquiry-filter-${s}`}
                    onClick={() => setInquiryStatusFilter(s)}
                    className={`text-[10px] font-mono uppercase tracking-[0.16em] px-3 py-1.5 border transition-colors ${active ? '' : 'text-[#666] border-[#1a1a1a] hover:border-[#333]'}`}
                    style={active ? { color: tone?.text || '#90E0EF', borderColor: tone?.border || '#90E0EF', background: tone?.bg || 'rgba(144,224,239,0.06)' } : undefined}
                  >
                    {s.replace('_', ' ')}
                    <span className="ml-2 opacity-60">{inquiryStats[s] ?? 0}</span>
                  </button>
                );
              })}
            </div>

            {/* Inquiry table */}
            <div className="border border-[#1a1a1a] bg-[#0a0a0a]">
              <div className="grid grid-cols-[56px_1.4fr_1fr_1fr_140px_120px_140px] gap-3 items-center px-4 py-2.5 border-b border-[#1a1a1a] text-[10px] font-mono uppercase tracking-[0.18em] text-[#555]">
                <span>Media</span>
                <span>Artwork / Artist</span>
                <span>From</span>
                <span>Country / Message</span>
                <span>Status</span>
                <span>Received</span>
                <span className="text-right">Actions</span>
              </div>

              {inquiries.length === 0 && (
                <div className="px-4 py-16 text-center">
                  <Inbox className="inline w-8 h-8 text-[#333]" />
                  <p className="text-[12px] font-outfit text-[#777] mt-3">No inquiries yet</p>
                  <p className="text-[11px] font-mono text-[#555] mt-1">Visitors&apos; messages will appear here.</p>
                </div>
              )}

              {inquiries.map((i) => (
                <div
                  key={i.id}
                  data-testid={`aw-inquiry-row-${i.id}`}
                  className="grid grid-cols-[56px_1.4fr_1fr_1fr_140px_120px_140px] gap-3 items-start px-4 py-3 border-b border-[#111]"
                >
                  <div className="w-10 h-10 border border-[#1a1a1a] bg-[#050505] overflow-hidden">
                    {i.artwork_thumbnail_url ? (
                      <img src={i.artwork_thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Frame className="w-3 h-3 text-[#333]" /></div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[12px] font-outfit text-white truncate">{i.artwork_title || 'Deleted artwork'}</div>
                    <div className="text-[10px] font-mono text-[#888] truncate">{i.artist_name || '—'}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[12px] text-white truncate">{i.name}</div>
                    <div className="text-[10px] font-mono text-[#90E0EF] truncate flex items-center gap-1">
                      {i.email}
                      <button
                        onClick={() => { navigator.clipboard.writeText(i.email); toast.success('Email copied'); }}
                        className="opacity-0 group-hover:opacity-100 hover:text-white transition-opacity"
                        title="Copy email"
                      ><Copy className="w-3 h-3" /></button>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono text-[#888]">{i.country || '—'}</div>
                    <div className="text-[11px] text-[#c8c8c8] truncate mt-0.5" title={i.message}>
                      {i.message || <em className="text-[#555]">no message</em>}
                    </div>
                  </div>
                  <div><InquiryPill status={i.status} /></div>
                  <div className="text-[10px] font-mono text-[#666]">
                    {new Date(i.created_at).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <select
                      data-testid={`aw-inquiry-status-${i.id}`}
                      value={i.status || 'new'}
                      onChange={(e) => setInquiryStatus(i.id, e.target.value)}
                      className="bg-[#0a0a0a] border border-[#1a1a1a] text-[10px] font-mono text-[#f6f6f6] px-2 py-1 uppercase tracking-[0.14em] focus:outline-none focus:border-[#90E0EF]/40"
                    >
                      {['new', 'contacted', 'in_progress', 'fulfilled', 'closed', 'spam'].map((s) => (
                        <option key={s} value={s}>{s.replace('_', ' ')}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <DeleteArtworkModal
        artworkId={deleteTarget?.id}
        artworkTitle={deleteTarget?.title}
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => {
          setDeleteTarget(null);
          setSelected((prev) => {
            const n = new Set(prev);
            n.delete(deleteTarget?.id);
            return n;
          });
          refresh();
        }}
      />
    </div>
  );
}
